import type { SentinelAnalyticsSelection } from './use-sentinel-selection';
import type { SentinelBreakdownRow, SentinelFilters } from './metric-keys';
import type { SentinelChannel } from '@/types/sentinelAnalytics';
import { CHANNELS, UNCATEGORIZED_CATEGORY } from './definitions';

/** kim-analytics' MAX_VALUES_PER_DIMENSION: it rejects a filter with more values. */
export const MAX_FILTER_VALUES = 100;

export interface FilterOptions {
  /** Busiest first, `UNCATEGORIZED_CATEGORY` last. */
  categories: string[];
  subCategories: string[];
  subCategoriesByCategory: Record<string, string[]>;
  /** The channels with tickets, in menu order. */
  channels: SentinelChannel[];
}

/** The menus' options from `category,subcategory` and `channel` breakdowns of the unfiltered range, which come busiest first. */
export function toFilterOptions(rows: SentinelBreakdownRow[], channelRows: SentinelBreakdownRow[]): FilterOptions {
  // A Map keeps the breakdown's order, which object keys don't for names like "2024".
  const subs = new Map<string, string[]>();
  for (const { dims } of rows) {
    if (!dims.category) continue;
    if (!subs.has(dims.category)) subs.set(dims.category, []);
    if (dims.subcategory) subs.get(dims.category)!.push(dims.subcategory);
  }
  return {
    categories: [...subs.keys(), UNCATEGORIZED_CATEGORY],
    subCategories: [...new Set([...subs.values()].flat())],
    subCategoriesByCategory: Object.assign(Object.create(null), Object.fromEntries(subs)),
    channels: CHANNELS.filter((channel) => channelRows.some(({ dims }) => dims.channel === channel)),
  };
}

const allOrSome = <T>(picked: T[], all: readonly T[]): T[] => (all.length > 0 && all.every((value) => picked.includes(value)) ? [] : picked);

const categoryValue = (category: string) => (category === UNCATEGORIZED_CATEGORY ? null : category);

/**
 * The picks as kim-analytics filters, which AND across dimensions. A category picked with every
 * sub-category is sent in full; one with only some is narrowed to those sub-categories.
 */
export function toDimensionFilters(
  { channels, categories, subCategories }: Pick<SentinelAnalyticsSelection, 'channels' | 'categories' | 'subCategories'>,
  options: FilterOptions,
  hide?: 'category' | 'channel',
): SentinelFilters {
  const filters: SentinelFilters = {};
  const pickedChannels = hide === 'channel' ? [] : allOrSome(channels, options.channels);
  if (pickedChannels.length > 0) filters.channel = pickedChannels;
  if (hide === 'category') return filters;

  const subsOf = (category: string) => options.subCategoriesByCategory[category] ?? [];
  const whole = categories.filter((category) => subsOf(category).every((sub) => subCategories.includes(sub)));
  const narrowing = subCategories.filter((sub) => !whole.some((category) => subsOf(category).includes(sub)));
  // A sub-category picked on its own brings its category, or the category filter would drop it.
  const owners = options.categories.filter((category) => subsOf(category).some((sub) => narrowing.includes(sub)));
  const everyCategoryWhole = narrowing.length === 0 && options.categories.every((category) => whole.includes(category));
  const picked = everyCategoryWhole ? [] : [...new Set([...categories, ...owners])];
  if (picked.length > 0) filters.category = picked.map(categoryValue);
  // Whole categories keep their tickets with no sub-category; a narrowed one's such tickets come along too.
  if (narrowing.length > 0) filters.subcategory = [...new Set([...narrowing, ...whole.flatMap(subsOf), ...(whole.length > 0 ? [null] : [])])];
  return filters;
}
